/**
 * Auth/session helpers shared by the route guards.
 *
 * Background (debug session google-sso-budget-401):
 * Amplify v6 `getCurrentUser()` only DECODES cached tokens — it performs no
 * server-side validation and does not refresh. After "delete all Cognito users
 * + wipe data", a browser can hold expired tokens for a now-deleted user under
 * the current app-client storage key. `getCurrentUser()` happily decodes those,
 * so the auth route guard passed and the calendar shell rendered, while every
 * data request failed (no usable token) — surfacing as the generic
 * "Could not load your budget data" error.
 *
 * `ensureValidSession()` fixes the root cause: it confirms the session can
 * actually produce a Cognito token (forcing a refresh when needed) and clears
 * stale tokens when it cannot, so a half-authenticated shell can never render.
 */
import { fetchAuthSession, signOut } from 'aws-amplify/auth';

// Captured ONCE per real page load (module init), before ensureValidSession() ever runs.
// beforeLoad can run more than once per navigation (e.g. React 19 StrictMode
// double-invocation) — checking window.location.search fresh on every call is not
// enough, because the first call's own URL cleanup (clearOAuthCallbackParams) would
// make a later call think it's no longer on the OAuth callback page. Freezing the
// answer at module load time makes the guard correct across every call in this load.
const pageLoadedAsOAuthCallback = isOAuthCallbackUrl();

/**
 * Returns true only when a usable Cognito access token can be obtained.
 *
 * On failure it best-effort clears the broken/stale session (signOut) so the
 * next navigation starts clean, then returns false. Callers should redirect to
 * the sign-in route when this returns false.
 *
 * Debug session google-sso-login-blank (2026-07-09): calling a bare `signOut()`
 * is unsafe while the current URL is itself the OAuth redirect callback
 * (`/login?code=...&state=...`). `fetchAuthSession()` already waits correctly
 * for an in-flight Amplify OAuth exchange, so there's no destructive race while
 * it's pending — the bug is what happens once the exchange has already failed
 * (bad/expired/reused code, network hiccup, etc.):
 *   1. Amplify only strips `?code=&state=` from the URL on the SUCCESS path
 *      (`clearHistory()` inside `completeOAuthFlow`); on failure the query
 *      string is left in the URL forever, stranding the user on a dead page.
 *   2. For a RETURNING Google user, Amplify's `signOut()` sees the stale
 *      `isOAuthSignIn` / `oauthMetadata.oauthSignIn` flags (persisted from
 *      their last successful federated sign-in) and takes its OAuth-aware
 *      branch, which performs a REAL full-page redirect to Cognito's
 *      `/logout` endpoint — a disruptive navigation fired from the sign-in
 *      page itself that has nothing to do with the just-failed attempt.
 * Both were confirmed via a live repro against the real Cognito domain.
 * Fix: when the URL is an OAuth callback, skip the `signOut()` call (there is
 * no session to clear — the exchange never produced tokens, and Amplify's own
 * failure handler already cleared its OAuth-specific storage keys) and just
 * clean the stale query string so the user lands on a fresh, retryable /login.
 */
export async function ensureValidSession(): Promise<boolean> {
  // Normal path: returns cached tokens, auto-refreshing if merely expired.
  let token = (await safeAccessToken()) ?? null;

  // If the cache yielded nothing usable, force a refresh once. This distinguishes
  // "expired but refreshable" (valid user) from "refresh fails" (deleted user).
  if (!token) {
    token = (await safeAccessToken(true)) ?? null;
  }

  if (token) return true;

  if (pageLoadedAsOAuthCallback) {
    // The OAuth exchange for this page load already failed (or never had tokens
    // to begin with) by the time we get here — signOut() has nothing useful to
    // clear and, for a returning Google user, would trigger an unwanted
    // federated logout redirect. Just clean the URL so a retry is possible.
    clearOAuthCallbackParams();
    return false;
  }

  // No usable session — clear any stale tokens left in storage.
  try {
    await signOut();
  } catch {
    /* best-effort */
  }
  return false;
}

/** True when the current URL is an Amplify OAuth Hosted-UI redirect callback. */
function isOAuthCallbackUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('code') && params.has('state');
}

/**
 * Strips the OAuth `code`/`state` query params, leaving the rest of the URL intact.
 *
 * Only touches history when those params are actually still present. `beforeLoad`
 * can run more than once per navigation (see `pageLoadedAsOAuthCallback` above), and
 * calling `history.replaceState` unconditionally on every call makes TanStack Router
 * treat each call as a fresh location change — which re-runs `beforeLoad` again,
 * which calls this again, forever. Making the no-op case a true no-op (skip
 * replaceState entirely once the params are already gone) breaks that loop.
 */
function clearOAuthCallbackParams(): void {
  if (typeof window === 'undefined' || typeof window.history === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('code') && !url.searchParams.has('state')) return;
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

async function safeAccessToken(forceRefresh = false): Promise<string | undefined> {
  try {
    const { tokens } = await fetchAuthSession(forceRefresh ? { forceRefresh: true } : undefined);
    return tokens?.accessToken?.toString();
  } catch {
    return undefined;
  }
}
